import type { Tables } from "@/integrations/supabase/types";

type Contact = Tables<"contacts">;

export interface DuplicateMatch {
  contact: Contact;
  confidence: "medium" | "high";
  reason: "exact" | "first_name" | "partial";
}

// Common titles/honorifics to exclude from name comparisons
const EXCLUDED_TITLES = new Set([
  "bro", "brother", "sis", "sister", "mr", "mrs", "ms", "miss", "madam",
  "pastor", "rev", "reverend", "dr", "prof", "professor", "father", "mother",
  "chief", "sir", "dame", "deacon", "deaconess", "elder", "bishop", "apostle",
  "prophet", "evangelist", "uncle", "auntie", "aunty", "cousin", "tio", "tia",
  "mum", "mom", "dad", "mummy", "daddy", "mama", "papa", "baba", "oga", "ma", "pa",
  "grandma", "grandpa", "nana", "granny", "pops", "my", "jr", "jnr", "snr", "senior", "junior",
  "alhaji", "alhaja", "hajia", "engr", "arch", "barr", "pharm", "surv",
  "otunba", "olori", "igwe", "obi", "eze", "emir", "lolo", "nze", "obong",
  "kabiyesi", "yeye", "baale", "high", "hon", "comrade", "doc"
]);

/**
 * Strips common titles from the beginning and end of a name
 */
function stripTitles(name: string): string {
  const parts = name.toLowerCase().trim().split(/\s+/);
  // Remove from start
  while (parts.length > 0 && EXCLUDED_TITLES.has(parts[0])) {
    parts.shift();
  }
  // Remove from end
  while (parts.length > 0 && EXCLUDED_TITLES.has(parts[parts.length - 1])) {
    parts.pop();
  }
  return parts.join(" ");
}

export function findSimilarContacts(
  inputName: string,
  existingContacts: Contact[],
  excludeId?: string
): DuplicateMatch[] {
  const strippedInput = stripTitles(inputName);
  if (strippedInput.length < 3) return [];

  const inputParts = strippedInput.split(/\s+/);
  const inputFirstName = inputParts[0];

  return existingContacts
    .filter((c) => c.id !== excludeId)
    .map((contact): DuplicateMatch | null => {
      const strippedExisting = stripTitles(contact.full_name);
      if (strippedExisting.length < 3) return null;
      
      const existingParts = strippedExisting.split(/\s+/);
      const existingFirstName = existingParts[0];

      // Exact full name match
      if (strippedExisting === strippedInput) {
        return { contact, confidence: "high", reason: "exact" };
      }

      // Same first name (min 3 chars)
      if (existingFirstName === inputFirstName && inputFirstName.length > 2) {
        return { contact, confidence: "medium", reason: "first_name" };
      }

      // Partial containment — only for meaningful substrings (3+ chars)
      if (
        (strippedInput.length >= 3 && strippedExisting.includes(strippedInput)) ||
        (existingFirstName.length >= 3 && strippedInput.includes(existingFirstName))
      ) {
        return { contact, confidence: "medium", reason: "partial" };
      }

      return null;
    })
    .filter((m): m is DuplicateMatch => m !== null);
}

export function upgradeBirthdayMatches(
  matches: DuplicateMatch[],
  inputBirthday: string | undefined
): DuplicateMatch[] {
  if (!inputBirthday) return matches;

  return matches.map((match) => {
    if (match.contact.birthday === inputBirthday) {
      return { ...match, confidence: "high" };
    }
    return match;
  });
}
